import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ChatCircleText, LinkSimple, ShieldCheck, Globe,
  Desktop, FileText, GearSix, Users,
  Lock, Buildings,
} from "@phosphor-icons/react";

const NAVY = "#0A0F2C";
const GOLD = "#C9A84C";

// Wordmark — plain gold text, no logo tile.
const BRAND_TEXT = "Fusion Agents";

const FEATURES = [
  { Icon: ChatCircleText, title: "Ask in plain language",
    sub: "No SQL, no tickets, no training — just type your question and get answers instantly" },
  { Icon: LinkSimple, title: "Connects to every ERP",
    sub: "Oracle Fusion, SAP, Dynamics, NetSuite, Workday and more — one AI for all" },
  { Icon: ShieldCheck, title: "Enterprise-grade security",
    sub: "SOC 2 certified, UAE PDPL compliant — your data never leaves your instance" },
  { Icon: Globe, title: "Built for GCC & India",
    sub: "Arabic language support, AED/SAR/INR data natively — regional AI that understands your market" },
];

const STATS = [
  { num: "90%",  lbl: "Fewer IT tickets for finance reports" },
  { num: "4 hrs", lbl: "Saved per user per week on ERP tasks" },
  { num: "1 day", lbl: "To go live on your Oracle instance" },
];

const PERSONAS = [
  { Icon: Desktop, label: "For CFOs" },
  { Icon: FileText, label: "For CIOs" },
  { Icon: GearSix, label: "For CPOs" },
  { Icon: Users, label: "For teams" },
];

const SYSTEMS = [
  { name: "Oracle Fusion", hl: true },
  { name: "SAP S/4HANA" }, { name: "MS Dynamics" }, { name: "NetSuite" },
  { name: "Workday" }, { name: "+ more" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr]           = useState("");
  const [loading, setLoading]   = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const user = await login(email, password);
      // ── KEY CHANGE: check is_first_login before routing ──
      nav(user.is_first_login ? "/change-password" : "/dashboard");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F0F2F5] py-10 px-4 font-sans">
      <div className="w-full max-w-[1080px] grid grid-cols-1 lg:grid-cols-[56%_44%] rounded-2xl overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.18)] bg-white"
        data-testid="login-shell">

        {/* ─────── LEFT (navy) ─────── */}
        <aside className="hidden lg:flex flex-col gap-6 p-11" style={{ backgroundColor: NAVY }} data-testid="login-left">
          {/* Wordmark */}
          <div className="flex items-center">
            <div className="text-[26px] font-semibold tracking-[0.5px]" style={{ color: GOLD }}>
              {BRAND_TEXT}
            </div>
          </div>

          <h1 className="text-[26px] font-semibold text-white leading-[1.35]" data-testid="login-headline">
            Your enterprise systems,<br />
            finally speaking <span style={{ color: GOLD }}>plain English.</span>
          </h1>

          <p className="text-[13.5px] leading-[1.75] max-w-[360px]" style={{ color: "#8B93B8" }}>
            One AI layer replaces complex ERP interfaces — so your CFO, CIO, and every team member gets instant answers from Oracle, SAP, and every system they run.
          </p>

          <div className="flex flex-col gap-[13px]">
            {FEATURES.map(({ Icon, title, sub }) => (
              <div key={title} className="flex items-start gap-2.5">
                <div className="w-[30px] h-[30px] rounded-[7px] flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "rgba(201,168,76,0.13)" }}>
                  <Icon size={16} weight="regular" style={{ color: GOLD }} />
                </div>
                <div>
                  <div className="text-[13px] font-semibold mb-0.5" style={{ color: "#E0E6FF" }}>{title}</div>
                  <div className="text-[11.5px] leading-[1.5]" style={{ color: "#8B93B8" }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2.5">
            {STATS.map((s) => (
              <div key={s.num} className="flex-1 rounded-[10px] px-3.5 py-3.5"
                style={{ backgroundColor: "rgba(255,255,255,0.055)", borderWidth: "0.5px",
                          borderStyle: "solid", borderColor: "rgba(255,255,255,0.1)" }}>
                <div className="text-[22px] font-semibold leading-none mb-1.5" style={{ color: GOLD }}>{s.num}</div>
                <div className="text-[10.5px] leading-[1.4]" style={{ color: "#8B93B8" }}>{s.lbl}</div>
              </div>
            ))}
          </div>

          <blockquote className="rounded-r-lg px-4 py-3.5" style={{ backgroundColor: "rgba(255,255,255,0.04)", borderLeft: `2.5px solid ${GOLD}` }}>
            <p className="text-[12.5px] italic leading-[1.65] mb-2" style={{ color: "#C8D0E8" }}>
              &ldquo;We used to raise 3 tickets a day just to get basic headcount reports. Now our HR director gets answers in seconds — no IT involvement.&rdquo;
            </p>
            <div className="text-[11px]" style={{ color: "#8B93B8" }}>
              <strong style={{ color: GOLD, fontStyle: "normal" }}>CFO, UAE-based manufacturing group</strong> — Oracle Fusion customer
            </div>
          </blockquote>

          <div>
            <div className="text-[10px] uppercase tracking-[0.9px] mb-2" style={{ color: "#5A6180" }}>Connects to</div>
            <div className="flex flex-wrap gap-[7px]">
              {SYSTEMS.map((s) => (
                <span key={s.name} className="rounded-md px-2.5 py-1 text-[10.5px] font-medium"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.055)",
                    borderWidth: "0.5px", borderStyle: "solid",
                    borderColor: s.hl ? "rgba(201,168,76,0.35)" : "rgba(255,255,255,0.1)",
                    color: s.hl ? GOLD : "#8B93B8",
                  }}>
                  {s.name}
                </span>
              ))}
            </div>
          </div>
        </aside>

        {/* ─────── RIGHT (white form) ─────── */}
        <section className="flex flex-col justify-center px-9 py-11 bg-white" data-testid="login-right">
          {/* Mobile-only wordmark */}
          <div className="lg:hidden flex items-center mb-6">
            <div className="text-[26px] font-semibold tracking-[0.5px]" style={{ color: GOLD }}>
              {BRAND_TEXT}
            </div>
          </div>

          <div className="flex flex-wrap gap-[7px] mb-6">
            {PERSONAS.map(({ Icon, label }) => (
              <span key={label} className="rounded-[20px] px-2.5 py-1 text-[11px] flex items-center gap-1"
                style={{
                  borderWidth: "0.5px", borderStyle: "solid",
                  borderColor: "rgba(201,168,76,0.5)",
                  backgroundColor: "rgba(201,168,76,0.08)",
                  color: "#8A6B1A",
                }}>
                <Icon size={12} weight="regular" style={{ color: "#8A6B1A" }} />
                {label}
              </span>
            ))}
          </div>

          <h2 className="text-[22px] font-semibold mb-1" style={{ color: "#0D1117" }} data-testid="login-title">Welcome back</h2>
          <p className="text-[13.5px] mb-6" style={{ color: "#666" }}>Sign in to your workspace</p>

          <form onSubmit={submit} className="space-y-[15px]" data-testid="login-form">
            <div>
              <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>Work email</label>
              <input
                data-testid="login-email"
                type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none transition-colors"
                style={{ border: "1px solid #DDE1E9", backgroundColor: "#FAFAFA", color: "#0D1117" }}
                onFocus={(e) => { e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.backgroundColor = "#fff"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "#DDE1E9"; e.currentTarget.style.backgroundColor = "#FAFAFA"; }}
              />
            </div>
            <div>
              <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>Password</label>
              <input
                data-testid="login-password"
                type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none transition-colors"
                style={{ border: "1px solid #DDE1E9", backgroundColor: "#FAFAFA", color: "#0D1117" }}
                onFocus={(e) => { e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.backgroundColor = "#fff"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "#DDE1E9"; e.currentTarget.style.backgroundColor = "#FAFAFA"; }}
              />
            </div>
            <div className="text-right text-[12px] -mt-2" style={{ color: "#888" }}>
              <Link to="/forgot-password" data-testid="login-forgot" className="hover:underline">Forgot password?</Link>
            </div>
            {err && <div className="text-[12px] font-semibold text-[#DC2626]" data-testid="login-error">{err}</div>}

            <button
              data-testid="login-submit"
              type="submit" disabled={loading}
              className="w-full h-[42px] rounded-lg text-[14px] font-semibold tracking-[0.3px] text-white transition-all duration-200 disabled:opacity-60"
              style={{ backgroundColor: NAVY, transform: "translateY(0)", boxShadow: "0 0 0 rgba(10,15,44,0)" }}
              onMouseOver={(e) => {
                if (loading) return;
                e.currentTarget.style.backgroundColor = "#141B40";
                e.currentTarget.style.transform = "translateY(-1.5px)";
                e.currentTarget.style.boxShadow = "0 8px 20px rgba(10,15,44,0.28)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = NAVY;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 0 0 rgba(10,15,44,0)";
              }}
              onMouseDown={(e) => { if (!loading) e.currentTarget.style.transform = "translateY(0px) scale(0.98)"; }}
              onMouseUp={(e) => { if (!loading) e.currentTarget.style.transform = "translateY(-1.5px) scale(1)"; }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="flex items-center gap-2.5 my-4">
            <hr className="flex-1" style={{ border: "none", borderTop: "0.5px solid #E5E8EE" }} />
            <span className="text-[12px]" style={{ color: "#999" }}>or continue with</span>
            <hr className="flex-1" style={{ border: "none", borderTop: "0.5px solid #E5E8EE" }} />
          </div>

          <button data-testid="login-sso"
            className="w-full h-10 rounded-lg flex items-center justify-center gap-2 text-[13px] font-medium hover:bg-gray-50 transition-colors"
            style={{ border: "1px solid #DDE1E9", color: "#333", backgroundColor: "#fff" }}>
            <Buildings size={15} weight="regular" style={{ color: "#555" }} />
            Sign in with Enterprise SSO
          </button>

          <div className="flex justify-center flex-wrap gap-4 mt-6">
            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "#999" }}>
              <Lock size={13} weight="regular" style={{ color: "#999" }} /> SSL secured
            </span>
            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "#999" }}>
              <ShieldCheck size={13} weight="regular" style={{ color: "#999" }} /> SOC 2 certified
            </span>
            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "#999" }}>
              <Globe size={13} weight="regular" style={{ color: "#999" }} /> GDPR &amp; UAE PDPL
            </span>
          </div>

          {/* Access is provisioned by admin — no self-register link */}
          <div className="text-center text-[13px] mt-5" style={{ color: "#888" }}>
            Don&apos;t have an account?{" "}
            <a href="#signup" data-testid="login-signup-cta"
              className="font-medium hover:underline" style={{ color: "#185FA5" }}>Request access</a>
          </div>
        </section>
      </div>
    </div>
  );
}